import html2canvas from 'html2canvas';
import { Board } from '../types';
import { User } from '../services/authService';

export const exportBoardAsJSON = (board: Board): void => {
  const jsonString = JSON.stringify(board, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const safeTitle = board.title.replace(/[<>:"/\\|?*]/g, '_');
  link.download = `${safeTitle}_BACKUP_${timestamp}.json`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const exportBoardAsCSV = (board: Board, user: User | null): void => {
  // Define CSV Report Details
  const reportMetadata = [
    ['Investigator', user?.name || 'Unknown'],
    ['Agency', user?.agency || 'N/A'],
    ['Badge Number', user?.badgeNumber || 'N/A'],
    ['Date Generated', new Date().toLocaleString()],
    ['Case/Board', board.title],
    [] // Empty row for spacing
  ];

  // Convert metadata to CSV rows
  const metadataRows = reportMetadata.map(row => row.map(opt => `"${opt}"`).join(','));

  // Define CSV headers
  const headers = ['ID', 'Label', 'Type', 'Tags', 'Notes', 'Creation Time'];

  // Map nodes to CSV rows
  const rows = board.nodes.map(node => {
    // Escape special characters in fields
    const escape = (text: string) => {
      if (!text) return '';
      return `"${text.replace(/"/g, '""')}"`; // Escape double quotes
    };

    return [
      escape(node.id),
      escape(node.label),
      escape(node.type),
      escape(node.tags.join(', ')),
      escape(node.notesMd),
      escape(new Date(board.createdAt).toISOString()) // Using board time as proxy if node doesn't have it
    ].join(',');
  });

  // Combine metadata, headers, and rows
  const csvContent = [
    ...metadataRows,
    headers.join(','),
    ...rows
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;

  // Timestamp for filename
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const safeTitle = board.title.replace(/[<>:"/\\|?*]/g, '_');
  link.download = `${safeTitle}_Nodes_${timestamp}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Generous timeout to ensure download starts
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const exportCanvasAsJPG = async (elementId: string, filename: string): Promise<void> => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Canvas element not found');
  }

  // Ensure filename has .jpg extension
  const cleanName = filename.replace(/\.(png|jpg|jpeg)$/i, '');
  const safeName = cleanName.replace(/[<>:"/\\|?*]/g, '_');
  const finalFilename = `${safeName}.jpg`;

  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#1F2937', // Dark background for JPG (no transparency)
      scale: 2, // High resolution
      useCORS: true,
      logging: false,
      allowTaint: true, // Try to allow tainted images if CORS fails
      ignoreElements: (element) => element.classList.contains('react-flow__minimap')
    });

    // Fallback function using Data URL if toBlob fails
    const downloadViaDataURL = () => {
      try {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = finalFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        console.error('Data URL fallback failed:', e);
        throw new Error('Failed to export image via both Blob and Data URL methods.');
      }
    };

    // Try Blob first
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.warn('Canvas toBlob returned null, using fallback...');
          downloadViaDataURL();
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = finalFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(url), 2000); // 2s delay for maximum safety
      }, 'image/jpeg', 0.9);
    } catch (e) {
      console.warn('Blob export failed, trying Data URL...', e);
      downloadViaDataURL();
    }

  } catch (error) {
    console.error('Error exporting canvas:', error);
    // User needs to know it failed
    throw error;
  }
};

export const importBoardFromJSON = (jsonString: string): Board => {
  try {
    const data = JSON.parse(jsonString);

    // Validate required fields
    if (!data.id || !data.title || !Array.isArray(data.nodes) || !Array.isArray(data.edges)) {
      throw new Error('Invalid board format');
    }

    // Validate node structure — filter out malformed nodes
    const validNodes = data.nodes.filter((node: any) =>
      node &&
      typeof node.id === 'string' &&
      typeof node.label === 'string' &&
      typeof node.type === 'string' &&
      node.position &&
      typeof node.position.x === 'number' &&
      typeof node.position.y === 'number'
    ).map((node: any) => ({
      ...node,
      tags: Array.isArray(node.tags) ? node.tags : [],
      attachments: Array.isArray(node.attachments) ? node.attachments : [],
      notesMd: node.notesMd || '',
      color: node.color || '#6B7280'
    }));

    // Validate edge structure — filter out malformed edges
    const validEdges = data.edges.filter((edge: any) =>
      edge &&
      typeof edge.id === 'string' &&
      typeof edge.source === 'string' &&
      typeof edge.target === 'string'
    );

    if (validNodes.length === 0 && data.nodes.length > 0) {
      throw new Error('No valid nodes found in imported file');
    }

    // Generate new ID for imported board
    const importedBoard: Board = {
      ...data,
      id: `board_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      nodes: validNodes,
      edges: validEdges,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      title: `${data.title} (Imported)`
    };

    return importedBoard;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to parse JSON file');
  }
};

/**
 * Exports a board as a single, self-contained HTML file for offline viewing.
 */
export const exportBoardAsPortableHTML = async (board: Board, user: User | null): Promise<void> => {
  // Deep clone to avoid mutating original
  const boardClone = JSON.parse(JSON.stringify(board));

  // EMBED IMAGES: Convert all remote image URLs to Base64 Data URIs
  // This ensures the HTML file parses images offline without CORS/Auth issues
  await Promise.all(boardClone.nodes.map(async (node: any) => {
    if (node.image && node.image.startsWith('http')) {
      try {
        // Fetch the image (CORS headers must be set on bucket, which we did!)
        const response = await fetch(node.image, { mode: 'cors' });
        const blob = await response.blob();

        // Convert to Base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        node.image = base64;
      } catch (error) {
        console.warn(`Failed to embed image for node ${node.label}:`, error);
        // Keep original URL as fallback, though it likely won't work offline
      }
    }
  }));

  const boardDataJson = JSON.stringify(boardClone).replace(/<\/script>/g, '<\\/script>');
  const invName = (user?.name || board.caseMetadata?.investigatorName || '').trim() || 'Unknown';
  const invAgency = (user?.agency || '').trim() || 'N/A';
  const invBadge = (user?.badgeNumber || '').trim() || 'N/A';
  const invDate = new Date().toLocaleString();

  // Build the inline script as a regular string to avoid template-in-template escaping issues
  const inlineScript = [
    'const board = ' + boardDataJson + ';',
    'const canvas = document.getElementById("canvas");',
    'const canvasContainer = document.getElementById("canvas-container");',
    'const edgesGroup = document.getElementById("edges-group");',
    'const tooltip = document.getElementById("tooltip");',
    'let zoom = 1, panX = 0, panY = 0, isDragging = false, startX, startY;',
    '',
    'function updateTransform() {',
    '  canvas.style.transform = "translate(" + panX + "px, " + panY + "px) scale(" + zoom + ")";',
    '  edgesGroup.style.transform = "translate(" + panX + "px, " + panY + "px) scale(" + zoom + ")";',
    '}',
    '',
    'function toggleTheme() {',
    '  document.body.classList.toggle("light-mode");',
    '  const isLight = document.body.classList.contains("light-mode");',
    '  document.getElementById("theme-toggle-text").innerText = isLight ? "Dark Mode" : "Light Mode";',
    '}',
    '',
    'function centerView() {',
    '  if (board.nodes.length === 0) return;',
    '  var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;',
    '  board.nodes.forEach(function(n) {',
    '    const w = n.width || (n.shape === "circle" ? 120 : 220);',
    '    const h = n.height || (n.shape === "circle" ? 120 : 100);',
    '    minX = Math.min(minX, n.position.x);',
    '    minY = Math.min(minY, n.position.y);',
    '    maxX = Math.max(maxX, n.position.x + w);',
    '    maxY = Math.max(maxY, n.position.y + h);',
    '  });',
    '  var centerX = (minX + maxX) / 2;',
    '  var centerY = (minY + maxY) / 2;',
    '  var containerW = canvasContainer.clientWidth;',
    '  var containerH = canvasContainer.clientHeight;',
    '  panX = containerW / 2 - centerX;',
    '  panY = containerH / 2 - centerY;',
    '  updateTransform();',
    '}',
    '',
    'canvasContainer.addEventListener("mousedown", function(e) {',
    '  if (e.target.id === "canvas-container" || e.target.id === "edge-layer") {',
    '    isDragging = true; startX = e.clientX - panX; startY = e.clientY - panY;',
    '  }',
    '});',
    'window.addEventListener("mousemove", function(e) {',
    '  if (isDragging) { panX = e.clientX - startX; panY = e.clientY - startY; updateTransform(); }',
    '});',
    'window.addEventListener("mouseup", function() { isDragging = false; });',
    '',
    'canvasContainer.addEventListener("wheel", function(e) {',
    '  e.preventDefault();',
    '  var delta = e.deltaY > 0 ? 0.9 : 1.1;',
    '  zoom = Math.min(Math.max(zoom * delta, 0.1), 3);',
    '  updateTransform();',
    '});',
    '',
    'function renderNodes() {',
    '  board.nodes.forEach(function(node) {',
    '    var el = document.createElement("div");',
    '    el.className = "node" + (node.shape === "circle" ? " circle" : "") + (node.customEntityType === "Note" ? " note" : "");',
    '    el.style.left = node.position.x + "px";',
    '    el.style.top = node.position.y + "px";',
    '    if (node.width) el.style.width = node.width + "px";',
    '    if (node.height) el.style.height = node.height + "px";',
    '    if (node.backgroundColor) el.style.backgroundColor = node.backgroundColor;',
    '    el.style.borderLeftColor = node.color;',
    '    el.style.borderLeftWidth = (node.shape === "circle" ? "1px" : "4px");',
    '    if (node.customEntityType === "Note") {',
    '      el.style.color = "#000000";',
    '    }',
    '    var tagsHtml = (node.tags || []).slice(0, 3).map(function(tag) {',
    '      return \'<span class="tag">\' + tag + "</span>";',
    '    }).join("");',
    '    var imageHtml = "";',
    '    if (node.image) {',
    '       imageHtml = \'<div class="node-image-container"><img src="\' + node.image + \'" style="width: 100%; height: 100%; object-fit: cover;"></div>\';',
    '    } else {',
    '       imageHtml = \'<div class="node-icon-placeholder"><span>◆</span></div>\';',
    '    }',
    '    var contentHtml = \'<div style="flex: 1; min-width: 0; display: flex; flex-direction: column; height: 100%;">\'',
    '      + \'<div class="node-label">\' + node.label + "</div>"',
    '      + \'<div class="node-type">\' + node.type + "</div>"',
    '      + \'<div class="flex flex-wrap gap-1 mt-auto">\' + tagsHtml + "</div>"',
    '      + "</div>";',
    '    el.innerHTML = imageHtml + contentHtml;',
    '',
    '    el.addEventListener("mouseenter", function(ev) {',
    '      tooltip.style.opacity = 1;',
    '      var tooltipImg = "";',
    '      if (node.image) {',
    '        tooltipImg = \'<div class="tooltip-image-container"><img src="\' + node.image + \'" style="width: 100%; height: 100%; object-fit: cover;"></div>\';',
    '      }',
    '      var notesHtml = "";',
    '      if (node.notesMd) {',
    '        notesHtml = \'<div class="tooltip-notes">\' + node.notesMd + "</div>";',
    '      }',
    '      var metaHtml = "";',
    '      if (node.attachments && node.attachments.length > 0) {',
    '         metaHtml += \'<div class="tooltip-meta"><span>📎 \' + node.attachments.length + \' files</span></div>\';',
    '      }',
    '      tooltip.innerHTML = tooltipImg + \'<div class="text-sm font-bold text-white mb-2 tooltip-title">\' + node.label + "</div>" + notesHtml + metaHtml;',
    '    });',
    '    el.addEventListener("mousemove", function(ev) {',
    '      tooltip.style.left = (ev.clientX + 20) + "px";',
    '      tooltip.style.top = (ev.clientY - 20) + "px";',
    '    });',
    '    el.addEventListener("mouseleave", function() { tooltip.style.opacity = 0; });',
    '    canvas.appendChild(el);',
    '  });',
    '}',
    '',
    'function renderEdges() {',
    '  board.edges.forEach(function(edge) {',
    '    var source = board.nodes.find(function(n) { return n.id === edge.source; });',
    '    var target = board.nodes.find(function(n) { return n.id === edge.target; });',
    '    if (!source || !target) return;',
    '',
    '    function getPos(node, handleId, isSource, otherNode) {',
    '       const w = node.width || (node.shape === "circle" ? 120 : 220);',
    '       const h = node.height || (node.shape === "circle" ? 120 : 100);',
    '       const x = node.position.x;',
    '       const y = node.position.y;',
    '',
    '       // Handle CustomNode IDs: t-s, t-t, b-s, b-t, l-s, l-t, r-s, r-t',
    '       if (handleId && handleId.startsWith("t")) return { x: x + w / 2, y: y, dir: "v" };',
    '       if (handleId && handleId.startsWith("b")) return { x: x + w / 2, y: y + h, dir: "v" };',
    '       if (handleId && handleId.startsWith("l")) return { x: x, y: y + h / 2, dir: "h" };',
    '       if (handleId && handleId.startsWith("r")) return { x: x + w, y: y + h / 2, dir: "h" };',
    '',
    '       // Smart Fallback if handleId is missing (e.g. click-to-connect edges)',
    '       if (!handleId && otherNode) {',
    '          const dx = (otherNode.position.x + (otherNode.width||220)/2) - (x + w/2);',
    '          const dy = (otherNode.position.y + (otherNode.height||100)/2) - (y + h/2);',
    '          if (Math.abs(dy) > Math.abs(dx)) {',
    '             if (dy > 0) return { x: x + w / 2, y: y + h, dir: "v" };',
    '             return { x: x + w / 2, y: y, dir: "v" };',
    '          } else {',
    '             if (dx > 0) return { x: x + w, y: y + h / 2, dir: "h" };',
    '             return { x: x, y: y + h / 2, dir: "h" };',
    '          }',
    '       }',
    '',
    '       // Fallback for old/default handles',
    '       if (handleId === "top") return { x: x + w / 2, y: y, dir: "v" };',
    '       if (handleId === "bottom") return { x: x + w / 2, y: y + h, dir: "v" };',
    '       if (handleId === "left") return { x: x, y: y + h / 2, dir: "h" };',
    '       if (handleId === "right") return { x: x + w, y: y + h / 2, dir: "h" };',
    '',
    '       if (isSource) return { x: x + w, y: y + h / 2, dir: "h" };',
    '       return { x: x, y: y + h / 2, dir: "h" };',
    '    }',
    '',
    '    var p1 = getPos(source, edge.sourceHandle, true, target);',
    '    var p2 = getPos(target, edge.targetHandle, false, source);',
    '',
    '    var x1 = p1.x, y1 = p1.y, x2 = p2.x, y2 = p2.y;',
    '    var path = document.createElementNS("http://www.w3.org/2000/svg", "path");',
    '    var pathStr = "";',
    '    ',
    '    // Ultra-forgiving straight line detection: if coordinates are reasonably close OR ',
    '    // if aligned and sharing direction',
    '    const isVeryCloseX = Math.abs(x1 - x2) < 5;',
    '    const isVeryCloseY = Math.abs(y1 - y2) < 5;',
    '    const isNearStraightX = Math.abs(x1 - x2) < 30;',
    '    const isNearStraightY = Math.abs(y1 - y2) < 30;',
    '',
    '    if (isVeryCloseX || isVeryCloseY || (p1.dir === "v" && p2.dir === "v" && isNearStraightX) || (p1.dir === "h" && p2.dir === "h" && isNearStraightY)) {',
    '      pathStr = "M " + x1 + " " + y1 + " L " + x2 + " " + y2;',
    '    } else {',
    '      var cx1 = x1, cy1 = y1, cx2 = x2, cy2 = y2;',
    '      var dx = Math.abs(x2 - x1) / 2;',
    '      var dy = Math.abs(y2 - y1) / 2;',
    '      if (p1.dir === "h") cx1 += (x2 > x1 ? dx : -dx);',
    '      else cy1 += (y2 > y1 ? dy : -dy);',
    '      if (p2.dir === "h") cx2 += (x1 > x2 ? dx : -dx);',
    '      else cy2 += (y1 > y2 ? dy : -dy);',
    '      pathStr = "M " + x1 + " " + y1 + " C " + cx1 + " " + cy1 + ", " + cx2 + " " + cy2 + ", " + x2 + " " + y2;',
    '    }',
    '    path.setAttribute("d", pathStr);',
    '    path.setAttribute("class", "edge-path");',
    '',
    '    if (edge.color) path.style.stroke = edge.color;',
    '',
    '    if (edge.label) {',
    '      var mid = document.createElementNS("http://www.w3.org/2000/svg", "text");',
    '      mid.setAttribute("x", String((x1 + x2) / 2));',
    '      mid.setAttribute("y", String((y1 + y2) / 2 - 8));',
    '      mid.setAttribute("text-anchor", "middle");',
    '      mid.setAttribute("class", "edge-label");',
    '      mid.textContent = edge.label;',
    '      edgesGroup.appendChild(mid);',
    '    }',
    '    edgesGroup.appendChild(path);',
    '  });',
    '}',
    '',
    'function renderRuler() {',
    '  var events = board.nodes.filter(function(n) { return n.tsStart; }).map(function(n) {',
    '    return { ts: n.tsStart, color: n.color, label: n.label, id: n.id };',
    '  }).sort(function(a, b) { return a.ts - b.ts; });',
    '  if (events.length === 0) return;',
    '  var start = events[0].ts, end = events[events.length - 1].ts;',
    '  var range = Math.max(end - start, 7 * 24 * 60 * 60 * 1000);',
    '  var extRange = range * 1.1;',
    '  var rulerEvents = document.getElementById("ruler-events");',
    '  var rulerContainer = document.getElementById("ruler-container");',
    '  var days = range / (24 * 60 * 60 * 1000);',
    '  var hours = range / (60 * 60 * 1000);',
    '  var majorInt, medInt, minorInt;',
    '  if (days > 730) { majorInt=365*24*3600000; medInt=90*24*3600000; minorInt=30*24*3600000; }',
    '  else if (days > 120) { majorInt=30*24*3600000; medInt=7*24*3600000; minorInt=24*3600000; }',
    '  else if (days > 14) { majorInt=7*24*3600000; medInt=24*3600000; minorInt=6*3600000; }',
    '  else if (hours > 24) { majorInt=24*3600000; medInt=6*3600000; minorInt=3600000; }',
    '  else { majorInt=3600000; medInt=15*60000; minorInt=60000; }',
    '  var tickStart = Math.floor(start / minorInt) * minorInt;',
    '  var tickEnd = end + range * 0.1;',
    '  for (var ts = tickStart; ts <= tickEnd; ts += minorInt) {',
    '    var x = ((ts - start) / extRange) * 100;',
    '    if (x < 0 || x > 100) continue;',
    '    var tick = document.createElement("div");',
    '    tick.className = "absolute bottom-[35px] w-px ruler-tick-line";',
    '    tick.style.left = x + "%";',
    '    var tier = 3;',
    '    if (ts % majorInt === 0) tier = 1;',
    '    else if (ts % medInt === 0) tier = 2;',
    '    tick.style.height = (tier === 1 ? 20 : tier === 2 ? 12 : 6) + "px";',
    '    if (tier === 1) {',
    '      var lbl = document.createElement("div");',
    '      lbl.className = "absolute top-full mt-1 text-[8px] ruler-text font-mono whitespace-nowrap -translate-x-1/2";',
    '      lbl.innerText = new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });',
    '      tick.appendChild(lbl);',
    '    }',
    '    rulerContainer.appendChild(tick);',
    '  }',
    '  events.forEach(function(ev) {',
    '    var x = ((ev.ts - start) / extRange) * 100;',
    '    var pip = document.createElement("div");',
    '    pip.className = "event-pip";',
    '    pip.style.left = x + "%";',
    '    pip.style.backgroundColor = ev.color;',
    '    pip.title = ev.label;',
    '    pip.addEventListener("click", function() {',
    '      var nd = board.nodes.find(function(n) { return n.id === ev.id; });',
    '      if (nd) {',
    '        panX = -nd.position.x * zoom + canvasContainer.clientWidth / 2;',
    '        panY = -nd.position.y * zoom + canvasContainer.clientHeight / 2;',
    '        updateTransform();',
    '      }',
    '    });',
    '    rulerEvents.appendChild(pip);',
    '  });',
    '}',
    '',
    'renderNodes();',
    'renderEdges();',
    'renderRuler();',
    'centerView();',
    'updateTransform();'
  ].join('\n');

  const htmlTemplate = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Fibre Investigation: ${board.title}</title>
    <script src="https://cdn.tailwindcss.com"><\/script>
    <style>
        :root {
            --bg-color: #0f172a;
            --header-bg: rgba(15, 23, 42, 0.5);
            --border-color: #334155;
            --text-main: #f8fafc;
            --text-dim: #94a3b8;
            --node-bg: rgba(30, 41, 59, 0.9);
            --timeline-bg: #1e293b;
            --grid-color: #334155;
            --tooltip-bg: rgba(15, 23, 42, 0.95);
        }

        body.light-mode {
            --bg-color: #f1f5f9;
            --header-bg: rgba(255, 255, 255, 0.8);
            --border-color: #cbd5e1;
            --text-main: #0f172a;
            --text-dim: #64748b;
            --node-bg: rgba(255, 255, 255, 0.9);
            --timeline-bg: #ffffff;
            --grid-color: #e2e8f0;
            --tooltip-bg: rgba(255, 255, 255, 0.98);
        }

        body { background-color: var(--bg-color); color: var(--text-main); font-family: ui-sans-serif, system-ui, sans-serif; overflow: hidden; height: 100vh; display: flex; flex-direction: column; transition: background-color 0.3s, color 0.3s; }
        #canvas-container { flex: 1; position: relative; cursor: grab; overflow: hidden; background-image: radial-gradient(var(--grid-color) 1px, transparent 1px); background-size: 30px 30px; }
        #canvas-container:active { cursor: grabbing; }
        #canvas { position: absolute; transform-origin: 0 0; will-change: transform; transition: transform 0.1s ease-out; }
        
        .node { position: absolute; padding: 12px; border-radius: 12px; border: 1px solid var(--border-color); background: var(--node-bg); backdrop-filter: blur(8px); width: 220px; min-height: 80px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); transition: box-shadow 0.2s, background-color 0.3s, border-color 0.3s; cursor: move; display: flex; align-items: center; gap: 12px; }
        .node.circle { border-radius: 50% !important; width: 120px; height: 120px; flex-direction: column; justify-content: center; text-align: center; }
        .node:hover { box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2); border-color: #475569; }
        
        .node-label { font-size: 0.75rem; font-weight: 700; color: var(--text-main); margin-bottom: 0.125rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .node-type { font-size: 9px; color: #3b82f6; font-family: monospace; text-transform: uppercase; margin-bottom: 0.25rem; }
        .tag { px-1; py-0.5; background: var(--bg-color); color: var(--text-dim); font-size: 8px; border-radius: 4px; border: 1px solid var(--border-color); }
        
        .node-image-container { width: 48px; height: 48px; flex-shrink: 0; border-radius: 6px; overflow: hidden; background: var(--bg-color); border: 1px solid var(--border-color); }
        .circle .node-image-container { width: 40px; height: 40px; border-radius: 50%; }
        .node-icon-placeholder { width: 40px; height: 40px; flex-shrink: 0; border-radius: 50%; background: var(--border-color); display: flex; align-items: center; justify-content: center; color: var(--text-dim); }
        .node.note .node-label { color: #000000 !important; }
        .node.note .node-type { color: #000000 !important; opacity: 0.7; }

        .edge-path { fill: none; stroke: var(--text-dim); stroke-width: 2; opacity: 0.6; }
        .edge-label { fill: var(--text-dim); font-size: 10px; }
        
        .tooltip { position: fixed; z-index: 100; pointer-events: none; opacity: 0; transition: opacity 0.2s; background: var(--tooltip-bg); border: 1px solid var(--border-color); padding: 12px; border-radius: 8px; width: 240px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5); }
        .tooltip-title { color: var(--text-main); }
        .tooltip-notes { font-size: 11px; color: var(--text-dim); line-height: 1.625; margin-bottom: 0.75rem; white-space: pre-wrap; display: -webkit-box; -webkit-line-clamp: 6; -webkit-box-orient: vertical; overflow: hidden; }
        .tooltip-image-container { width: 100%; height: 120px; margin-bottom: 8px; border-radius: 6px; overflow: hidden; background: var(--bg-color); border: 1px solid var(--border-color); }

        #timeline { height: 120px; background: var(--timeline-bg); border-top: 1px solid var(--border-color); display: flex; flex-direction: column; z-index: 50; transition: background-color 0.3s; }
        .ruler-tick-line { background: var(--border-color); }
        .ruler-text { color: var(--text-dim); }
        .event-pip { position: absolute; width: 12px; height: 12px; border-radius: 3px; bottom: 45px; transform: translateX(-50%); cursor: pointer; border: 1px solid rgba(0,0,0,0.1); }
        
        .theme-toggle { padding: 2px 8px; border-radius: 6px; border: 1px solid var(--border-color); font-size: 10px; font-weight: 600; color: var(--text-main); background: var(--header-bg); cursor: pointer; transition: all 0.2s; }
        .theme-toggle:hover { background: var(--border-color); }
    </style>
</head>
<body>
    <header class="p-3 border-b border-[var(--border-color)] bg-[var(--header-bg)] flex justify-between items-center transition-colors">
        <div>
            <h1 class="text-lg font-bold text-[var(--text-main)] flex items-center gap-2">
                <span class="text-blue-500">◆</span> Fibre: ${board.title}
            </h1>
            <p class="text-[10px] text-[var(--text-dim)]">Portable Investigation Export • V1.2 - Ultra Fix</p>
        </div>
        <div class="text-right text-[10px] text-[var(--text-dim)] font-mono flex flex-col items-end gap-1">
            <p class="leading-none">INVESTIGATOR: ${invName}</p>
            <p class="leading-none">AGENCY: ${invAgency} | BADGE: ${invBadge}</p>
            <div class="flex items-center gap-3 leading-none">
                <button onclick="toggleTheme()" class="theme-toggle">
                    <span id="theme-toggle-text">Light Mode</span>
                </button>
                <div class="uppercase">DATE: ${invDate}</div>
            </div>
        </div>
    </header>

    <div id="canvas-container">
        <svg id="edge-layer" style="position: absolute; width: 100%; height: 100%; overflow: visible; pointer-events: none;">
            <defs></defs>
            <g id="edges-group"></g>
        </svg>
        <div id="canvas"></div>
    </div>

    <div id="timeline">
        <div class="flex items-center justify-between px-4 py-2 border-b border-[var(--border-color)]">
            <span class="text-[10px] font-bold uppercase tracking-widest text-[var(--text-dim)]">Timeline</span>
        </div>
        <div class="flex-1 relative overflow-hidden" id="ruler-container">
            <div id="ruler-line" class="absolute bottom-[35px] inset-x-0 h-px bg-[var(--border-color)]"></div>
            <div id="ruler-events" class="h-full w-full relative px-10"></div>
        </div>
    </div>

    <div id="tooltip" class="tooltip"></div>

    <script>${inlineScript}<\/script>
</body>
</html>`;

  const blob = new Blob([htmlTemplate], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const safeTitle = board.title.replace(/[<>:"/\\|?*]/g, '_');
  link.download = `${safeTitle}_REPORT_${timestamp}.html`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};